<template>
  <!-- `attach="#app"`: o Tailwind sai com `important: '#app'` (padrão do PlaceholderDoctorBlockedModal). -->
  <v-dialog
    :model-value="modelValue"
    max-width="640"
    scrollable
    attach="#app"
    scrim="rgba(22, 22, 29, 0.55)"
    aria-labelledby="qualification-blocked-title"
    data-testid="qualification-blocked-dialog"
    @update:model-value="onDialogUpdate"
  >
    <div class="qualification-dialog">
      <header class="qualification-dialog__header">
        <span
          class="qualification-dialog__icon qualification-dialog__icon--error"
          aria-hidden="true"
        >
          <svg viewBox="0 0 24 24" width="24" height="24">
            <path :d="mdiShieldLockOutline" fill="currentColor" />
          </svg>
        </span>
        <div class="qualification-dialog__heading">
          <h2 id="qualification-blocked-title" class="qualification-dialog__title">
            Não é possível alocar este médico
          </h2>
          <span v-if="doctorName" class="qualification-dialog__chip">
            <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
              <path :d="mdiAccountOutline" fill="currentColor" />
            </svg>
            {{ doctorName }}
          </span>
        </div>
      </header>

      <div class="qualification-dialog__scroll">
        <p class="qualification-dialog__intro" data-testid="qualification-blocked-message">
          {{ doctorName ?? 'O médico' }} não atende à Qualificação exigida desta escala.
        </p>

        <div class="qualification-dialog__body">
          <template v-if="blocking.length">
            <p class="qualification-dialog__section">O que falta</p>
            <QualificationMissingList :missing="blocking" />
          </template>

          <template v-if="preAllocatable.length">
            <p class="qualification-dialog__section">Também falta (permitiria pré-alocação)</p>
            <QualificationMissingList :missing="preAllocatable" />
            <p class="qualification-dialog__note">
              Como há pelo menos um critério que <strong>bloqueia</strong>, a pré-alocação não está
              disponível para este médico.
            </p>
          </template>

          <p
            v-if="!missing.length"
            class="qualification-dialog__note"
            data-testid="qualification-blocked-fallback"
          >
            Não foi possível listar o que falta agora. Confira os documentos do médico.
          </p>

          <div class="qualification-dialog__help">
            <p>
              O médico já vê essa pendência no <strong>Drive do Médico</strong>, no app. Assim que o
              Time de Documentação aprovar o documento, a alocação fica liberada.
            </p>
            <p>Não há liberação manual: a regra vem da Qualificação exigida da escala.</p>
          </div>
        </div>
      </div>

      <footer class="qualification-dialog__footer">
        <FilledButton
          height="44px"
          width="140px"
          color="primary"
          class="bg-primary30"
          data-testid="qualification-blocked-ok"
          @click="close"
        >
          Entendi
        </FilledButton>
      </footer>
    </div>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { mdiAccountOutline, mdiShieldLockOutline } from '@mdi/js'

import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import { QualificationOnFail } from '@/types/qualification-eligibility.types'
import type { EligibilityReason } from '@/types/qualification-eligibility.types'

import QualificationMissingList from './QualificationMissingList.vue'

const props = defineProps<{
  modelValue: boolean
  doctorName: string | null
  missing: EligibilityReason[]
}>()
const emit = defineEmits<{ 'update:modelValue': [value: boolean] }>()

const hasBlocking = computed(() =>
  props.missing.some((reason) => reason.onFail === QualificationOnFail.BLOCK)
)
// Sem item que bloqueia (corpo inesperado), tudo vai para "O que falta".
const blocking = computed(() =>
  hasBlocking.value
    ? props.missing.filter((reason) => reason.onFail === QualificationOnFail.BLOCK)
    : props.missing
)
const preAllocatable = computed(() =>
  hasBlocking.value
    ? props.missing.filter((reason) => reason.onFail === QualificationOnFail.PRE_ALLOCATE)
    : []
)

function close(): void {
  emit('update:modelValue', false)
}

function onDialogUpdate(open: boolean): void {
  if (!open) close()
}
</script>

<style scoped src="./qualificationDialog.css"></style>
